import { useCallback } from "react";

import { DashboardModel, type DashboardsAccessMetadataModel } from "@doitintl/cmp-models";
import { getCollection, modelServerTimestamp, type ModelTimestamp } from "@doitintl/models-any";

export const Metadata = "metadata";
export const DashboardsAccessMetadataCollection = "dashboardsAccessMetadata";

const refreshThresholdMilliseconds = 1000 * 60 * 60 * 6;

export const getDashboardMetadataId = (customerId: string, organizationId: string, dashboardId: string) =>
  `${customerId}_${organizationId}_${dashboardId}`;

export const getDashboardMetadataRef = (customerId: string, organizationId: string, dashboardId: string) => {
  const id = getDashboardMetadataId(customerId, organizationId, dashboardId);
  return getCollection(DashboardModel).doc("metadata").collection(DashboardsAccessMetadataCollection).doc(id);
};

export const useDashboardsMetadata = (customerId: string, organizationId: string, dashboardId: string | null) => {
  const checkRefreshNeeded = useCallback(async (): Promise<boolean> => {
    if (!dashboardId) {
      return false;
    }
    const metadataRef = getDashboardMetadataRef(customerId, organizationId, dashboardId);
    const snapshot = await metadataRef.get();
    const data = snapshot.data() as DashboardsAccessMetadataModel | undefined;

    if (!data?.timeLastRefreshed) {
      return true;
    }

    const timeLastRefreshed = data.timeLastRefreshed.toDate().getTime();
    return Date.now() > timeLastRefreshed + refreshThresholdMilliseconds;
  }, [customerId, organizationId, dashboardId]);

  const handleDashboardMetadataUpdate = useCallback(async (): Promise<void> => {
    if (!dashboardId) {
      return;
    }
    const metadataRef = getDashboardMetadataRef(customerId, organizationId, dashboardId);
    const timeLastAccessed = modelServerTimestamp() as ModelTimestamp;

    const metadata: DashboardsAccessMetadataModel = {
      customerId,
      organizationId,
      dashboardId,
      timeLastAccessed,
    };

    await metadataRef.set(metadata, { merge: true });
  }, [customerId, organizationId, dashboardId]);

  return {
    handleDashboardMetadataUpdate,
    checkRefreshNeeded,
  };
};
